/**
 * CalendarHeatmap Component
 * GitHub-style calendar heatmap showing activity over a year
 * Supports both day-of-week rows and secondary dimension rows (region, city, etc.)
 */

import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  CalendarHeatmapDataDto,
  CalendarDayDto,
  CalendarHeatmapWithDimensionDataDto,
  CalendarRowDto,
  CalendarCellDto
} from '@/api/types/dtos/analytics/AnalyticsDto';

interface CalendarHeatmapProps {
  data: CalendarHeatmapDataDto | CalendarHeatmapWithDimensionDataDto;
  onClick: (filter: string) => void;
}

// Type guard to detect secondary dimension data
const isDimensionData = (data: any): data is CalendarHeatmapWithDimensionDataDto => {
  return data.rows !== undefined;
};

const getWeeks = (year: string): Date[][] => {
  const weeks: Date[][] = [];
  const jan1 = new Date(parseInt(year), 0, 1);
  const firstDayOfWeek = new Date(jan1);
  firstDayOfWeek.setDate(jan1.getDate() - jan1.getDay()); // Start from Sunday

  const currentWeek: Date[] = [];
  const currentDate = new Date(firstDayOfWeek);

  // Create 52-53 weeks
  for (let i = 0; i < 53; i++) {
    const currentWeek: Date[] = [];
    for (let day = 0; day < 7; day++) {
      currentWeek.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(currentWeek);
  }

  return weeks;
};

const getLevelColor = (level: number): string => {
  const colors = [
    '#ebedf0',
    '#c6e48b',
    '#7bc96f',
    '#239a3b',
    '#196127',
  ];
  return colors[level] || colors[0];
};

const formatMonthDay = (date: Date): string => {
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

// Helper function to group weeks by month
const groupWeeksByMonth = (weeks: Date[][], year: string): Array<{month: string, weekIndices: number[]}> => {
  const monthGroups: Array<{month: string, weekIndices: number[]}> = [];
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
                      'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  // Find the first week that contains each month
  const monthStartWeeks = new Map<number, number>();

  monthNames.forEach((_, monthIndex) => {
    // Find the first week that contains any day from this month
    const weekIndex = weeks.findIndex(week =>
      week.some(date =>
        date.getFullYear() === parseInt(year) &&
        date.getMonth() === monthIndex
      )
    );

    if (weekIndex !== -1) {
      monthStartWeeks.set(monthIndex, weekIndex);
    }
  });

  // Group weeks by month: each month owns weeks from its start until the next month starts
  const sortedMonthStarts = Array.from(monthStartWeeks.entries())
    .sort((a, b) => a[1] - b[1]); // Sort by week index

  for (let i = 0; i < sortedMonthStarts.length; i++) {
    const [monthIndex, startWeek] = sortedMonthStarts[i];
    const endWeek = i < sortedMonthStarts.length - 1
      ? sortedMonthStarts[i + 1][1] // Next month starts here
      : weeks.length; // Or end of year

    // Collect all weeks from startWeek to endWeek
    const weekIndices = [];
    for (let w = startWeek; w < endWeek; w++) {
      weekIndices.push(w);
    }

    if (weekIndices.length > 0) {
      monthGroups.push({
        month: monthNames[monthIndex],
        weekIndices
      });
    }
  }

  return monthGroups;
};

export const CalendarHeatmap: React.FC<CalendarHeatmapProps> = ({ data, onClick }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg')); // <1280px (14 inches)

  // Safety check for data - DON'T return early, use conditional rendering instead
  const hasValidData = data && data.year;
  const isDimension = hasValidData && isDimensionData(data);
  const isLegacy = hasValidData && !isDimension && (data as CalendarHeatmapDataDto).days;

  const weeks = useMemo(() => hasValidData ? getWeeks(data.year) : [], [data?.year]);
  const legacyData = isLegacy ? (data as CalendarHeatmapDataDto) : null;

  // Create a map for quick lookup
  const dayMap = useMemo(() => {
    const map = new Map<string, CalendarDayDto>();
    if (legacyData?.days) {
      legacyData.days.forEach((day) => {
        map.set(day.date, day);
      });
    }
    return map;
  }, [legacyData?.days]);

  // Group weeks by month for month labels
  const monthGroups = useMemo(() => groupWeeksByMonth(weeks, hasValidData ? data.year : ''), [weeks, hasValidData ? data.year : '']);

  // Calculate current week index for highlighting
  const currentWeekIndex = useMemo(() => {
    if (!hasValidData) return -1;
    const today = new Date();
    const yearNum = parseInt(data.year);
    if (today.getFullYear() !== yearNum) return -1;

    // Find which week contains today
    return weeks.findIndex(week =>
      week.some(date =>
        date.toDateString() === today.toDateString()
      )
    );
  }, [weeks, hasValidData ? data.year : '']);

  // Early return only if absolutely no data (but hooks are already called)
  if (!hasValidData) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography>No calendar data available</Typography>
      </Paper>
    );
  }

  // Check if this is dimension data or legacy data
  if (isDimension) {
    return <CalendarHeatmapWithDimension data={data as CalendarHeatmapWithDimensionDataDto} onClick={onClick} isSmallScreen={isSmallScreen} />;
  }

  // Legacy day-of-week calendar
  if (!isLegacy) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography>No calendar data available</Typography>
      </Paper>
    );
  }

  const handleCellClick = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayData = dayMap.get(dateStr);
    if (dayData && dayData.count > 0) {
      onClick(dateStr);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        borderWidth: 0,
        borderColor: 'rgba(0, 0, 0, 0)',
        borderImage: 'none',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        {/* <Typography variant="subtitle1">{data.label}</Typography> */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: getLevelColor(1),
                borderRadius: 3,
              }}
            />
            <Typography variant="caption">Menos</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: getLevelColor(4),
                borderRadius: 3,
              }}
            />
            <Typography variant="caption">Más</Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable wrapper - only shows scrollbar when content overflows */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'visible',
          paddingTop: '10px',
          paddingBottom: '10px',
        }}
      >
        {/* Calendar container with month labels, week numbers, and day cells */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.75,
          width: 'fit-content',

          alignItems: 'flex-start',
          position: 'relative',
        }}>
        {/* Day of week labels - vertical */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pr: 1, width: 80, flexShrink: 0, paddingTop: '45px' }}>
          {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((day) => (
            <Box
              key={day}
              sx={{
                width: 70,
                height: 12,
                fontSize: '0.85rem',
                color: 'text.primary',
                fontWeight: 'fontWeightMedium',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Month labels layer - positioned absolutely over the week columns */}
        <Box sx={{
          position: 'absolute',
          left: 80 + 4, // day labels width + one gap
          top: -5,
          height: 20,
          display: 'flex',
          pointerEvents: 'none',
        }}>
          {monthGroups.map((group) => {
            // Calculate center position: middle of this month's week span
            const colWidth = 19.5; // 14px cell + 6px gap
            const startWeek = group.weekIndices[0];
            const endWeek = group.weekIndices[group.weekIndices.length - 1];
            const midWeek = (startWeek + endWeek) / 2;
            const leftPos = midWeek * colWidth;

            return (
              <Box
                key={group.month}
                sx={{
                  position: 'absolute',
                  left: leftPos + 7, // +7 to center on the column (half of 14px cell)
                  transform: 'translateX(-50%)',
                  fontSize: '0.85rem',
                  color: 'text.primary',
                  fontWeight: 'fontWeightBold',
                  whiteSpace: 'nowrap',
                }}
              >
                {group.month}
              </Box>
            );
          })}
        </Box>

        {/* Week columns with numbers and day cells */}
        {weeks.map((week, weekIndex) => {
          const isCurrentWeek = weekIndex === currentWeekIndex;

          return (
            <Box
              key={weekIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                flexShrink: 0,
                paddingTop: '20px',
                position: 'relative',
                // Wider highlighter background for current week (like a highlighter marker)
                ...(isCurrentWeek && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: -3,
                    top: 22, // Start after week number (14px + 6px gap = 20px)
                    width: 18,
                    bottom: -4,
                    bgcolor: '#AFBEFF', // Amber/yellow highlighter
                    borderRadius: 2,
                    border: '1px solid #5271ff',
                    pointerEvents: 'none',
                    zIndex: 0,
                  },
                }),
              }}
            >
            {/* Week number */}
            <Box sx={{
              height: 12,
              fontSize: '0.75rem',
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
              marginTop: 1
            }}>
              {weekIndex + 1}
            </Box>

            {/* Day cells */}
            {week.map((date, dayIndex) => {
              const dateStr = date.toISOString().split('T')[0];
              const dayData = dayMap.get(dateStr);
              const level = dayData?.level ?? 0;
              const count = dayData?.count ?? 0;

              return (
                <Tooltip
                  key={dayIndex}
                  title={`${dateStr}: ${count} fincas`}
                  arrow
                >
                  <Box
                    onClick={() => handleCellClick(date)}
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: getLevelColor(level),
                      borderRadius: 3,
                      cursor: dayData && dayData.count > 0 ? 'pointer' : 'default',
                      '&:hover': {
                        opacity: dayData && dayData.count > 0 ? 0.8 : 1,
                      },
                      flexShrink: 0,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          );
        })}
        </Box>
      </Box>

      {/* Year label */}
      <Typography variant="caption" sx={{ mt: 1,
                color: 'primary.contrastText',
                fontWeight: 'fontWeightBold', 
                backgroundColor: '#AFBEFF', 
                width: 'fit-content', 
                padding: '1px 10px', 
                borderRadius: '12px', 
                flexShrink: 0, 
                textAlign: 'center',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,  }}>
        {data.year}
      </Typography>
    </Paper>
  );
};

/**
 * Calendar Heatmap with Secondary Dimension
 * Shows rows grouped by categorical field (region, city, etc.) instead of day of week
 */
interface CalendarHeatmapWithDimensionProps {
  data: CalendarHeatmapWithDimensionDataDto;
  onClick: (filter: string) => void;
  isSmallScreen: boolean;
}

const CalendarHeatmapWithDimension: React.FC<CalendarHeatmapWithDimensionProps> = ({
  data,
  onClick,
  isSmallScreen
}) => {
  const theme = useTheme();

  // Truncate label if too long
  const truncateLabel = (label: string): string => {
    if (label.length > 15) {
      return label.substring(0, 12) + '...';
    }
    return label;
  };

  // Group weeks by month for month labels (reuse existing logic)
  const weeksForMonthLabels = useMemo(() => {
    const weeks: Date[][] = [];
    const jan1 = new Date(parseInt(data.year), 0, 1);
    const firstDayOfWeek = new Date(jan1);
    firstDayOfWeek.setDate(jan1.getDate() - jan1.getDay());

    const currentDate = new Date(firstDayOfWeek);
    for (let i = 0; i < 53; i++) {
      const currentWeek: Date[] = [];
      for (let day = 0; day < 7; day++) {
        currentWeek.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(currentWeek);
    }
    return weeks;
  }, [data.year]);

  const monthGroups = useMemo(() => groupWeeksByMonth(weeksForMonthLabels, data.year), [weeksForMonthLabels, data.year]);

  // Calculate current week index for highlighting
  const currentWeekIndex = useMemo(() => {
    const today = new Date();
    const yearNum = parseInt(data.year);
    if (today.getFullYear() !== yearNum) return -1;

    // Find which week contains today (same logic as original heatmap)
    return weeksForMonthLabels.findIndex(week =>
      week.some(date =>
        date.toDateString() === today.toDateString()
      )
    );
  }, [weeksForMonthLabels, data.year]);

  const handleCellClick = (rowValue: string, weekIndex: number, cell: CalendarCellDto, year: string) => {
    if (cell.count > 0) {
      // Pass dimension value, week, and year as structured JSON filter
      onClick(JSON.stringify({ dimensionValue: rowValue, week: weekIndex + 1, year }));
    }
  };

  // Calculate max row label width
  const maxLabelWidth = useMemo(() => {
    const maxLength = Math.max(...data.rows.map(r => r.rowLabel.length));
    return Math.min(Math.max(maxLength * 7, 80), 150); // Min 60px, max 150px
  }, [data.rows]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        borderWidth: 0,
        borderColor: 'rgba(0, 0, 0, 0)',
        borderImage: 'none',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        {/* <Typography variant="subtitle1">{data.label}</Typography> */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: getLevelColor(1), borderRadius: 3 }} />
            <Typography variant="caption">Menos</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: getLevelColor(4), borderRadius: 3 }} />
            <Typography variant="caption">Más</Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable wrapper - only shows scrollbar when content overflows */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'visible',
          paddingTop: '10px',
          paddingBottom: '10px',
        }}
      >
        {/* Calendar container with row labels and week columns */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.75,
          width: 'fit-content',
          alignItems: 'flex-start',
          position: 'relative',
        }}>
        {/* Row labels - vertical */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          pr: 1,
          width: maxLabelWidth,
          flexShrink: 0,
          paddingTop: '45px',
        }}>
          {data.rows.map((row) => (
            <Tooltip key={row.rowValue} title={row.rowLabel}>
              <Box
                sx={{
                  height: 14,
                  fontSize: '0.85rem',
                  color: 'text.primary',
                  fontWeight: 'fontWeightMedium',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateLabel(row.rowLabel)}
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* Month labels layer - positioned absolutely over the week columns */}
        <Box sx={{
          position: 'absolute',
          left: maxLabelWidth + 4,
          top: -5,
          height: 20,
          display: 'flex',
          pointerEvents: 'none',
        }}>
          {monthGroups.map((group) => {
            const colWidth = 19.5; // 14px cell + 6px gap
            const startWeek = group.weekIndices[0];
            const endWeek = group.weekIndices[group.weekIndices.length - 1];
            const midWeek = (startWeek + endWeek) / 2;
            const leftPos = midWeek * colWidth;

            return (
              <Box
                key={group.month}
                sx={{
                  position: 'absolute',
                  left: leftPos + 7,
                  transform: 'translateX(-50%)',
                  fontSize: '0.85rem',
                  color: 'text.primary',
                  fontWeight: 'fontWeightBold',
                  whiteSpace: 'nowrap',
                }}
              >
                {group.month}
              </Box>
            );
          })}
        </Box>

        {/* Week columns with numbers and cells */}
        {data.weeks.map((week, weekIndex) => {
          const isCurrentWeek = weekIndex === currentWeekIndex;

          return (
            <Box
              key={week}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                flexShrink: 0,
                paddingTop: '20px',
                position: 'relative',
                ...(isCurrentWeek && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: -3,
                    top: 22,
                    width: 18,
                    bottom: -4,
                    bgcolor: '#AFBEFF',
                    borderRadius: 2,
                    border: '1px solid #5271ff',
                    pointerEvents: 'none',
                    zIndex: 0,
                  },
                }),
              }}
            >
              {/* Week number */}
              <Box sx={{
                height: 12,
                fontSize: '0.75rem',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
                marginTop: 1
              }}>
                {week}
              </Box>

              {/* Cells for each row */}
              {data.rows.map((row, rowIndex) => {
                const cell = row.cells[weekIndex];
                const level = cell?.level ?? 0;
                const count = cell?.count ?? 0;

                return (
                  <Tooltip
                    key={`${row.rowValue}-${week}`}
                    title={`${row.rowLabel}, Week ${week}: ${count} fincas`}
                    arrow
                  >
                    <Box
                      onClick={() => handleCellClick(row.rowValue, weekIndex, cell, data.year)}
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: getLevelColor(level),
                        borderRadius: 3,
                        cursor: count > 0 ? 'pointer' : 'default',
                        '&:hover': {
                          opacity: count > 0 ? 0.8 : 1,
                        },
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          );
        })}
        </Box>
      </Box>

      {/* Year label */}
      <Typography variant="caption" sx={{ mt: 1, color: 'primary.contrastText',fontWeight: 'fontWeightBold', backgroundColor: '#AFBEFF', width: 'fit-content', padding: '1px 10px', borderRadius: '12px', flexShrink: 0  }}>
        {data.year}
      </Typography>
    </Paper>
  );
};


